import { useEffect, useState, useCallback } from "react";
import { ShieldCheck, ShieldOff, KeyRound, Copy } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { inputCls, Label } from "./shared";

export function SecurityPanel() {
  const [status, setStatus] = useState(null); // { enabled, backup_codes_remaining }
  const [setup, setSetup] = useState(null);    // { secret, qr, otpauth_uri }
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [backupCodes, setBackupCodes] = useState(null);
  const [disableCode, setDisableCode] = useState("");

  const load = useCallback(() => api.get("/admin/2fa/status").then((r) => setStatus(r.data)).catch(() => setStatus({ enabled: false })), []);
  useEffect(() => { load(); }, [load]);

  const beginSetup = async () => {
    setBusy(true);
    try { const r = await api.post("/admin/2fa/setup"); setSetup(r.data); setBackupCodes(null); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const enable = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/2fa/enable", { code: code.trim() });
      setBackupCodes(r.data.backup_codes);
      setSetup(null); setCode("");
      toast.success("Two-factor authentication enabled");
      await load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const disable = async () => {
    if (!window.confirm("Disable two-factor authentication for the admin account?")) return;
    setBusy(true);
    try {
      await api.post("/admin/2fa/disable", { code: disableCode.trim() });
      setDisableCode(""); setBackupCodes(null);
      toast.success("Two-factor authentication disabled");
      await load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const copyCodes = () => { navigator.clipboard?.writeText((backupCodes || []).join("\n")); toast.success("Backup codes copied"); };

  const enabled = !!status?.enabled;

  return (
    <div data-testid="security-panel" className={`mb-6 border p-4 rounded-sm ${enabled ? "border-emerald-500/60 bg-emerald-950/20" : "border-amber-900/30 bg-zinc-950"}`}>
      <div className="flex items-center gap-2 mb-3">
        {enabled ? <ShieldCheck size={16} className="text-emerald-400" /> : <ShieldOff size={16} className="text-amber-400" />}
        <h3 className="font-cinzel text-amber-300 uppercase tracking-widest text-sm">Admin Two-Factor Authentication</h3>
        {enabled && (
          <span data-testid="twofa-active-badge" className="ml-auto flex items-center gap-1.5 text-xs text-emerald-300 font-semibold">
            <KeyRound size={13} /> Enabled · {status.backup_codes_remaining} backup codes left
          </span>
        )}
      </div>

      {/* One-time backup codes shown after enabling */}
      {backupCodes && (
        <div data-testid="twofa-backup-codes" className="mb-4 border border-amber-700/50 bg-amber-950/20 p-3 rounded-sm">
          <p className="text-sm text-amber-200 mb-2 font-semibold">Save these one-time backup codes somewhere safe — they won't be shown again.</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 font-mono text-sm text-amber-100 mb-2">
            {backupCodes.map((c) => <span key={c} className="bg-zinc-900 border border-zinc-700 px-2 py-1 rounded-sm text-center tracking-wider">{c}</span>)}
          </div>
          <button data-testid="copy-backup-codes" onClick={copyCodes} className="flex items-center gap-1.5 text-xs text-amber-300 hover:text-amber-200 underline"><Copy size={12} /> Copy all</button>
        </div>
      )}

      {!enabled && !setup && (
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-sm text-zinc-400 flex-1 min-w-[220px]">Require a 6-digit code from an authenticator app (Google Authenticator, Authy) in addition to your password when signing into the admin account.</p>
          <button data-testid="twofa-begin-setup-btn" onClick={beginSetup} disabled={busy}
            className="px-5 py-2 bg-emerald-500 text-zinc-950 font-cinzel uppercase tracking-wide text-sm hover:bg-emerald-400 disabled:opacity-50 rounded-sm">Set Up 2FA</button>
        </div>
      )}

      {!enabled && setup && (
        <div className="flex flex-col sm:flex-row gap-5 items-start">
          <div className="shrink-0">
            <img data-testid="twofa-qr" src={setup.qr} alt="Scan with your authenticator app" className="w-44 h-44 bg-white p-2 rounded-sm" />
          </div>
          <div className="flex-1 min-w-[220px]">
            <p className="text-sm text-zinc-400 mb-1">1. Scan this QR code with Google Authenticator (or enter the key manually).</p>
            <p className="text-[11px] text-zinc-500 mb-3 break-all">Manual key: <span className="font-mono text-amber-300">{setup.secret}</span></p>
            <Label>2. Enter the 6-digit code to confirm</Label>
            <div className="flex gap-2">
              <input data-testid="twofa-confirm-code" className={inputCls + " tracking-[0.3em] text-center max-w-[160px]"} value={code}
                onChange={(e) => setCode(e.target.value)} placeholder="000000" inputMode="numeric" />
              <button data-testid="twofa-enable-btn" onClick={enable} disabled={busy || !code.trim()}
                className="px-5 py-2 bg-emerald-500 text-zinc-950 font-cinzel uppercase tracking-wide text-sm hover:bg-emerald-400 disabled:opacity-50 rounded-sm">Enable</button>
            </div>
            <button data-testid="twofa-cancel-setup" onClick={() => { setSetup(null); setCode(""); }} className="mt-2 text-xs text-zinc-500 hover:text-amber-400 underline">Cancel</button>
          </div>
        </div>
      )}

      {enabled && (
        <div className="flex flex-wrap items-end gap-3">
          <p className="text-sm text-zinc-400 w-full">Your admin login is protected by an authenticator app. To turn it off, confirm with a current code (or a backup code).</p>
          <div className="w-44"><Label>Authenticator / backup code</Label>
            <input data-testid="twofa-disable-code" className={inputCls + " tracking-widest"} value={disableCode} onChange={(e) => setDisableCode(e.target.value)} placeholder="000000" /></div>
          <button data-testid="twofa-disable-btn" onClick={disable} disabled={busy || !disableCode.trim()}
            className="px-4 py-2 border border-red-900/50 text-red-300 hover:border-red-500 text-sm rounded-sm disabled:opacity-50">Disable 2FA</button>
        </div>
      )}
    </div>
  );
}
